import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, BACKEND_URL } from "@/lib/api";
import { LogOut, Menu, X, ShieldOff } from "lucide-react";
import ForumNotifBell from "@/components/ForumNotifBell";
import TenantSwitcher from "@/components/TenantSwitcher";

const TENANT_ACCENT = {
  dcc: "#F26A21",
  "banco-alfa": "#F26A21",
  "demo-bank": "#10B981",
};

// Fase 7 · white-label branding for the sidebar/header
function BrandBlock({ branding, tenantName, isWhiteLabel, size = "sm" }) {
  const logoUrl = branding?.logo_url;
  const src = logoUrl?.startsWith("/api") ? `${BACKEND_URL}${logoUrl}` : logoUrl;
  if (isWhiteLabel && src) {
    return (
      <div className="flex items-center gap-2" data-testid="brand-block-wl">
        <img
          src={src}
          alt={tenantName || "logo"}
          className={size === "sm" ? "h-8 w-auto object-contain" : "h-10 w-auto object-contain"}
        />
        <div className="hidden sm:block">
          <p className="text-[10px] uppercase tracking-widest text-gray-400 leading-none">Campus</p>
          <p className="text-sm font-bold text-white leading-tight">{tenantName}</p>
        </div>
      </div>
    );
  }
  return <Logo size={size} dark />;
}

export default function DashboardLayout({ title, navItems, tenantSlug = "dcc", children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [twofaDisabled, setTwofaDisabled] = useState(false);
  const [tenantMeta, setTenantMeta] = useState(null);
  const isWhiteLabel = tenantMeta?.is_white_label === true;
  const branding = tenantMeta?.branding || {};
  const wlPrimary = branding?.primary_color;
  const accent = (isWhiteLabel && wlPrimary) ? wlPrimary : (TENANT_ACCENT[tenantSlug] || "#B84A0C");

  useEffect(() => {
    api.get("/config/public").then((r) => {
      if (r.data?.require_2fa === false) setTwofaDisabled(true);
    }).catch(() => {});
    // Fase 7 · fetch tenant branding to support white-label
    if (tenantSlug && tenantSlug !== "dcc") {
      api.get(`/tenants/${tenantSlug}/public`).then((r) => setTenantMeta(r.data)).catch(() => setTenantMeta(null));
    }
  }, [tenantSlug]);

  const NavList = ({ onClick }) => (
    <>
      <p className="text-[10px] uppercase tracking-widest text-gray-500 px-3 py-2">{title}</p>
      {navItems.map((n) => (
        <NavLink
          key={n.to || n.label}
          to={n.to || "#"}
          end={n.end}
          onClick={onClick}
          data-testid={`side-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
              isActive ? "bg-white/5 text-white" : "text-gray-400 hover:bg-white/[0.03] hover:text-white"
            }`
          }
          style={({ isActive }) => isActive ? { borderLeft: `3px solid ${accent}` } : {}}
        >
          {n.icon && <n.icon className="w-4 h-4" />}
          {n.label}
          {n.badge && <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-gray-700 text-gray-300">{n.badge}</span>}
        </NavLink>
      ))}
    </>
  );

  return (
    <div className="h-screen flex overflow-hidden bg-[#0B0F14]" style={{ "--tenant-accent": accent }}>
      {/* Desktop sidebar · scroll independiente */}
      <aside className="hidden md:flex md:w-64 flex-col shrink-0 h-screen bg-[#111827] border-r border-gray-800">
        <div className="p-6 border-b border-gray-800 shrink-0">
          <BrandBlock branding={branding} tenantName={tenantMeta?.name} isWhiteLabel={isWhiteLabel} size="sm" />
          <div className="mt-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: accent }} />
            <span className="text-[11px] uppercase tracking-widest font-semibold text-gray-400">
              {isWhiteLabel && tenantMeta?.name ? tenantMeta.name : tenantSlug}
            </span>
          </div>
        </div>
        <nav className="flex-1 min-h-0 p-3 space-y-1 overflow-y-auto sidebar-scroll"><NavList /></nav>
        {isWhiteLabel && (
          <div
            data-testid="cobranding-footer-desktop"
            className="shrink-0 px-4 pt-3 pb-2 border-t border-gray-800/60 flex items-center gap-2 opacity-70"
          >
            <p className="text-[10px] uppercase tracking-widest text-gray-500 leading-tight">
              Programa acreditado por
            </p>
            <Logo size="xs" dark />
          </div>
        )}
        <div className="shrink-0 p-4 border-t border-gray-800 bg-[#111827]">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="min-w-0 flex-1">
              <p className="text-sm text-white font-semibold truncate">{user?.full_name}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
            <ForumNotifBell />
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={() => nav("/")} className="text-xs px-2 py-1 border border-gray-700 rounded text-gray-300 hover:bg-white/5">
              Inicio
            </button>
            <button onClick={logout} data-testid="dashboard-logout-btn" className="text-xs px-2 py-1 border border-gray-700 rounded text-gray-300 hover:bg-white/5 flex items-center gap-1">
              <LogOut className="w-3 h-3" /> Salir
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {twofaDisabled && (
          <div
            data-testid="demo-2fa-banner"
            className="shrink-0 hidden md:flex items-center justify-center gap-1.5 px-4 py-1 bg-[#0B0F14] border-b border-gray-800 text-[10px] uppercase tracking-widest text-gray-500"
          >
            <ShieldOff className="w-3 h-3" /> Modo demo · 2FA desactivada
          </div>
        )}
        {/* Mobile header */}
        <header className="shrink-0 md:hidden sticky top-0 z-30 border-b border-gray-800 bg-[#111827] px-4 py-3 flex items-center justify-between">
          <Link to="/"><BrandBlock branding={branding} tenantName={tenantMeta?.name} isWhiteLabel={isWhiteLabel} size="sm" /></Link>
          <div className="flex items-center gap-1">
            <TenantSwitcher variant="mobile" />
            <ForumNotifBell />
            <button data-testid="dashboard-mobile-menu-btn" aria-label="menu" onClick={() => setMobileOpen(true)} className="text-gray-200 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center">
              <Menu />
            </button>
          </div>
        </header>

        {/* Desktop topbar with TenantSwitcher (Fase 14.2) */}
        <div className="shrink-0 hidden md:flex items-center justify-end gap-3 px-6 py-2 border-b border-gray-800 bg-[#0B0F14]">
          <TenantSwitcher variant="topbar" />
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="md:hidden fixed inset-0 z-40" onClick={() => setMobileOpen(false)}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <aside
              className="absolute right-0 top-0 h-full w-72 bg-[#111827] border-l border-gray-800 flex flex-col"
              onClick={(e) => e.stopPropagation()}
              data-testid="dashboard-mobile-drawer"
            >
              <div className="p-4 border-b border-gray-800 flex items-center justify-between shrink-0">
                <BrandBlock branding={branding} tenantName={tenantMeta?.name} isWhiteLabel={isWhiteLabel} size="sm" />
                <button onClick={() => setMobileOpen(false)} className="text-gray-300 p-1" aria-label="cerrar">
                  <X />
                </button>
              </div>
              <div className="p-4 border-b border-gray-800 shrink-0">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: accent }} />
                  <span className="text-[11px] uppercase tracking-widest font-semibold text-gray-400">{tenantSlug}</span>
                </div>
                <p className="mt-2 text-sm text-white font-semibold">{user?.full_name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
              <nav className="flex-1 min-h-0 p-3 space-y-1 overflow-y-auto sidebar-scroll"><NavList onClick={() => setMobileOpen(false)} /></nav>
              {isWhiteLabel && (
                <div
                  data-testid="cobranding-footer-mobile"
                  className="shrink-0 px-4 pt-3 pb-2 border-t border-gray-800/60 flex items-center gap-2 opacity-70"
                >
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 leading-tight">
                    Programa acreditado por
                  </p>
                  <Logo size="xs" dark />
                </div>
              )}
              <div className="shrink-0 p-4 border-t border-gray-800 flex gap-2">
                <button onClick={() => { setMobileOpen(false); nav("/"); }} className="text-xs px-3 py-1.5 border border-gray-700 rounded text-gray-300 hover:bg-white/5">
                  Inicio
                </button>
                <button onClick={() => { setMobileOpen(false); logout(); }}
                  data-testid="dashboard-mobile-logout-btn"
                  className="text-xs px-3 py-1.5 border border-gray-700 rounded text-gray-300 hover:bg-white/5 flex items-center gap-1">
                  <LogOut className="w-3 h-3" /> Salir
                </button>
              </div>
            </aside>
          </div>
        )}

        {/* Main content · scroll independiente */}
        <main className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden main-scroll p-4 sm:p-6 md:p-10">{children}</main>
      </div>
    </div>
  );
}
